/* eslint-disable react/display-name */

import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import { clone } from 'ramda';
import { Cropper } from 'react-cropper';
import {
  DataGrid,
  Drawer,
  TextInputWithLabel,
  Button,
  FileUploader,
  Loader
} from 'quantux-ui';
import { blue, gray, salmon } from '../../theme';
import { BASE_URL, DRAWER_BREAK_POINT } from '../../utils';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';

const DEBUG_MODE = true;

const DOCS_PER_PAGE = 15;

const DEFAULT_CELL_STYLE = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const ICONS = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const INIT_HEADER_DEFS = [
  // Company Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Company Name',
    field: 'companyName',
    key: 'companyName',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Mailing address ()
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Mailing Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => (
      <div>
        <a
          href={`tel:+1${text}`}
        >
          {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
        </a>
      </div>
    )
  },
  // Users
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Company Users',
    field: 'companyUsers',
    key: 'companyUsers',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      console.log('Names', text);
      const names = text.split('_');
      const comps = [];
      names.forEach((el, idx) => {
        comps.push(
          <div key={`${idx}_${Date.now()}`}>
            {el}
          </div>
        );
      });
      return (
        <React.Fragment>
          {comps}
        </React.Fragment>
      );
    }
  }
];

const CompaniesDataGrid = (props) => {
  // -- Grid Data
  const [showLoader, setShowLoader] = useState(false);
  const [gridHeaderDefs, setGridHeaderDefs] = useState(INIT_HEADER_DEFS);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  // -- Grid Layout
  const [showCardsGrid, setShowCardsGrid] = useState(false);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 884);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 143);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileLayout, setMobileLayout] = useState(false);
  // -- Grid Ellipsis Menu
  const [showGridEllipsisMenu, setShowGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  // -- Add Company drawer
  const [showAddCompanyDrawer, setShowAddCompanyDrawer] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [companyName, setCompanyName] = useState('');
  const [avatarImageUrl, setAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
  const [displayName, setDisplayName] = useState('');
  const [isDisplayNameValid, setIsDisplayNameValid] = useState(false);
  const [isDisplayNamePristine, setIsDisplayNamePristine] = useState(true);
  const [userName, setUserName] = useState('');
  const [isUserNameValid, setIsUserNameValid] = useState(false);
  const [isUserNamePristine, setIsUserNamePristine] = useState(true);
  const [isUserNameDuplicate, setIsUserNameDuplicate] = useState(true);
  const [streetAddress, setStreetAddress] = useState('');
  const [suite, setSuite] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailPristine, setIsEmailPristine] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(true);
  const [phone, setPhone] = useState('');
  const [isPhonePristine, setIsPhonePristine] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [fax, setFax] = useState('');
  const [website, setWebsite] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [canAddCompany, setCanAddCompany] = useState(false);
  // -- Banner & Avatar Cropper Drawers
  const [showBannerCropperDrawer, setShowBannerCropperDrawer] = useState('');
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [showAvatarCropperDrawer, setShowAvatarCropperDrawer] = useState('');
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);

  // -- Refs ------------------------------------------------------------
  const gridParentRef = useRef();
  const drawerBannerRef = useRef();
  const bannerCropperRef = useRef();
  const bannerFileInputRef = useRef();
  const drawerAvatarRef = useRef();
  const avatarCropperRef = useRef();
  const avatarFileInputRef = useRef();

  // -- Route History ---------------------------------------------------
  const history = useHistory();

  // -- Styles ----------------------------------------------------------
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      width: `${gridWidth}px`,
      overflow: 'hidden',
      height: showCardsGrid ? `${window.innerHeight - 90}px` : ''
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    drawerContainer: {
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    }
  });

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.9 * gridParentRefWidth);
      console.log(`GridWidth = ${tmp}`);
      setGridWidth(Math.min(tmp, 884));
      setGridHeight(window.innerHeight - 128);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // -- Network Ops -----------------------------------------------------
  const getData = async () => {
    setShowLoader(true);
    // Companies data reqeust
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    reqHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

    const formData = new FormData();
    formData.append('skip', '10');
    formData.append('limit', '80');

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/company/companies`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/company/count`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting companies grid data and count');
        console.log(err);
      });

    if (response) {
      const companyDataResponse = await response[0].json();
      const companyCountResponse = await response[1].json();

      if (companyCountResponse && companyDataResponse.success) {
        console.log(companyCountResponse);
        setDocsCount(companyCountResponse.companyCount);
      }

      if (companyDataResponse && companyDataResponse.success) {
        const data = [];
        for (let i = 0; i < companyDataResponse.companies.length; i++) {
          const activeRecord = companyDataResponse.companies[i];
          const add1 = activeRecord.mailingAddress.streetAddress1;
          const suite = '';
          const city = activeRecord.mailingAddress.city;
          const state = activeRecord.mailingAddress.state;
          const zip = activeRecord.mailingAddress.zip;
          let companyUsers = '';
          console.log(activeRecord.users);
          activeRecord.users.forEach((el, idx) => {
            companyUsers += el.fullName;
            if (idx !== activeRecord.users.length - 1) {
              companyUsers += '_';
            }
          });

          data.push({
            id: activeRecord._id,
            companyName: `${activeRecord.companyName}`,
            mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
            phone: activeRecord.phone,
            npi: `${activeRecord.npi}`,
            companyUsers
          });
        }
        setGridData(data);
        setShowLoader(false);
      }
    }
  };

  // #region -- Effects -------------------------------------------------

  // -- Load grid data
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Check for duplicate username
  useEffect(() => {
    if (userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/company/isCompanyUserNameExists`);
      const params = { userName };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          setIsUserNameDuplicate(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [userName]);

  // Can Add Company flag check
  useEffect(() => {
    const value =
      displayName.length >= 2 && displayName.length <= 40 &&
      userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0 &&
      !isUserNameDuplicate &&
      isEmailValid &&
      !isEmailDuplicate
    setCanAddCompany(value);
  }, [
    displayName,
    userName,
    isUserNameDuplicate,
    email,
    isEmailValid,
    isEmailDuplicate
  ]);
  // #endregion

  // #region Helpers
  const resetState = () => {
    setShowAddCompanyDrawer(false);
    setBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
    setCompanyName('');
    setAvatarImageUrl('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
    setDisplayName('');
    setIsDisplayNameValid(false);
    setIsDisplayNamePristine(true);
    setUserName('');
    setIsUserNameValid(false);
    setIsUserNamePristine(true);
    setIsUserNameDuplicate(true);
    setStreetAddress('');
    setSuite('');
    setCity('');
    setState('');
    setZip('');
    setEmail('');
    setIsEmailPristine(true);
    setIsEmailValid(false);
    setIsEmailDuplicate(true);
    setPhone('');
    setIsPhonePristine(true);
    setIsPhoneValid(false);
    setFax('');
    setWebsite('');
    setProfileDescription('');
    setCanAddCompany(false);
    setShowBannerCropperDrawer('');
    setShowBannerCropper(false);
    setIsBannerUploading(false);
    setShowAvatarCropperDrawer('');
    setShowAvatarCropper(false);
    setIsAvatarUploading(false);
  };
  // #endregion

  // #region Grid Toolbar Event Handlers
  const handleOnClickResetFilters = () => {
    // Reset grid data & filters
    console.log('Reset grid data and filters');
  };

  const handleOnClickAddCompany = () => {
    setShowAddCompanyDrawer(true);
  };
  // #endregion

  // #region Add Company Drawer
  const closeAddCompanyDrawer = () => {
    setShowAddCompanyDrawer(false);
    resetState();
  };

  const handleOnEditCompanyName = (e) => {
    const { value } = e.target;
    setCompanyName(value);
  };

  const handleOnEditDisplayName = (e) => {
    const { value } = e.target;
    setDisplayName(value);
  };

  const handleOnEditUserName = (e) => {
    const { value } = e.target;
    setUserName(value);
  };

  const handleOnEditStreetAddress = (e) => {
    const { value } = e.target;
    setStreetAddress(value);
  };

  const handleOnEditSuite = (e) => {
    const { value } = e.target;
    setSuite(value);
  };

  const handleOnEditCity = (e) => {
    const { value } = e.target;
    setCity(value);
  };

  const handleOnEditState = (e) => {
    const { value } = e.target;
    setState(value);
  };

  const handleOnEditZip = (e) => {
    const { value } = e.target;
    setZip(value);
  };

  const handleOnEditEmail = (e) => {
    const { value } = e.target;
    setEmail(value);
  };

  const handleOnEditPhone = (e) => {
    const { value } = e.target;
    setPhone(value);
  };

  const handleOnEditFax = (e) => {
    const { value } = e.target;
    setFax(value);
  };

  const handleOnEditWebsite = (e) => {
    const { value } = e.target;
    setWebsite(value);
  };

  const handleOnChangeProfileDescription = (e) => {
    const { value } = e.target;
    setProfileDescription(value);
  };

  // Create new company
  const handleOnClickSaveCompanyProfile = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('companyName', companyName);
    formData.append('displayName', displayName);
    formData.append('userName', userName);
    formData.append('streetAddress', streetAddress);
    formData.append('suite', suite);
    formData.append('city', city);
    formData.append('state', state);
    formData.append('zip', zip);
    formData.append('email', email);
    formData.append('phone', phone);
    formData.append('fax', fax);
    formData.append('website', website);
    formData.append('avatarImageUrl', avatarImageUrl);
    formData.append('bannerImageUrl', bannerImageUrl);
    formData.append('profileDescription', profileDescription);

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/company/create`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          closeAddCompanyDrawer();
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region Banner Upload Drawer
  const uploadBanner = () => {
    const formData = new FormData();
    const img = bannerFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBanner = (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'croppedBanner.png');
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped banner image url', res.fileUrl);
          setBannerImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBannerUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileSelected = () => {
    if (bannerFileInputRef.current.files.length > 0) {
      setIsBannerUploading(true);
      uploadBanner();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBannerCrop = () => {
    const imageElement = bannerCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerBannerRef.current.src = url;
      uploadCroppedBanner(blob);
      setShowBannerCropperDrawer(false);
    });
  };
  // #endregion

  // #region Avatar Upload Drawer
  const uploadAvatar = () => {
    const formData = new FormData();
    const img = avatarFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedAvatar = (blob) => {
    const formData = new FormData();
    formData.append('file', blob);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped avatar image url', res.fileUrl);
          setAvatarImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsAvatarUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileSelected = () => {
    if (avatarFileInputRef.current.files.length > 0) {
      setIsAvatarUploading(true);
      uploadAvatar();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickAvatarCrop = () => {
    const imageElement = avatarCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerAvatarRef.current.src = url;
      uploadCroppedAvatar(blob);
      setShowAvatarCropperDrawer(false);
    });
  };
  // #endregion

  // #region Grid Event Handlers

  /**
   * Grid Cell onClick event
   * @param {String} col - Grid column of clicked cell
   * @param {String} value - Value of clicked cell
   * @param {String} recordId - Database _id of cell clicked row
   */
  const handleOnClickCell = (col, value, recordId) => {
    if (col === 'companyName') {
      const idx = gridData.findIndex(el => el.companyName === value);
      const companyId = gridData[idx].id;
      localStorage.setItem('lastBookmark', '/admin/companies');
      history.push(`/company/${companyId}/profile`);
    }
  };

  /**
   * Handle on click Grid Ellipsis Menu
   * @param {Object} e - Click event object
   * @param {Number} idx - Row Index of ellipsis menu
   */
  const handleOnPopupMenu = (e, idx) => {
    setShowGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  /**
   * Update filter type of grid column
   * @param {String} field - Header def field for which filter type change is required
   * @param {String} value - New filter type of header def field
   */
  const handleFilterTypeUpdate = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    if (updatedFilters[field]) {
      updatedFilters[field].filterType = value;
      updatedFilters[field].value = value !== 'dateRange' ? '' : ':';
    }
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filterType = value;
        el.filter = true;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    setFilters(updatedFilters);
  };

  /**
   * Apply filter to grid data
   * Note: Some grid filters are applied automatically, and in some cases when we need
   * to manually trigger a filter, this event is used.
   * @param {String} dataType
   * @param {String} filterType
   * @param {String} field
   * @param {String} filterValue
   */
  const handleOnApplyFilter = (dataType, filterType, field, filterValue) => {
    setFilters((prvState) => {
      const prvStateClone = clone(prvState);
      if (filterValue === '' && Object.keys(prvStateClone).indexOf(field) > -1) {
        delete prvStateClone[field];
      }
      const newFilter = {};
      if (filterValue !== '' && filterValue !== ':') {
        newFilter[field] = { dataType, filterType, value: filterValue };
      }

      const result = {
        ...prvStateClone,
        ...newFilter
      };

      if (typeof DEBUG_MODE !== 'undefined' && DEBUG_MODE) {
        console.log('--------------------- Filters ---------------------');
        console.log(result);
      }

      return result;
    });
  };

  /**
   * Toggle filter for a grid column
   * @param {String} field - Field for which filter state is updated
   * @param {Boolean} value - Filter state, true means ON and false means OFF
   */
  const handleOnFilterToggle = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filter = value;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    if (updatedFilters[field]) {
      delete updatedFilters[field];
    }
    setFilters(updatedFilters);
  };

  /**
   * Update grid column pin side
   * @param {String} field - Grid column for which pin update is required
   * @param {String} pinValue - New pin value (Left, Right, None)
   */
  const handleOnPinChange = (field, pinValue) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.pin = pinValue;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
  };

  /**
   * Load page for grid
   * @param {Number} page - Page number to load
   */
  const handleOnLoadPage = (page) => {
    setCurrentPage(page);
  };

  /**
   * Update grid column sort order. Cycles through ascending, descending & none
   * @param {String} field - Grid column for which sort order change is required
   */
  const handleOnSortOrderChanged = (field) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.sortOrder = el.sortOrder === 0
          ? el.sortOrder + 1
          : el.sortOrder + (-el.sortOrder * 2);
      } else {
        el.sortOrder = 0;
      }
    });

    const sort = {};
    for (let i = 0; i < updatedGridHeaderDefs.length; i++) {
      if (updatedGridHeaderDefs[i].sortOrder === 0) {
        continue;
      } else {
        sort[updatedGridHeaderDefs[i].field] = updatedGridHeaderDefs[i].sortOrder;
      }
    }

    setGridHeaderDefs(updatedGridHeaderDefs);
    setSorting(sort);
  };
  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Add Company Drawer ---------------------------------------- */}
      {showAddCompanyDrawer &&
        <Drawer
          placement="right"
          visible={showAddCompanyDrawer}
          toggleDrawer={closeAddCompanyDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-building' />
                <div className='noselect'>Create Company Profile</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={closeAddCompanyDrawer}
              />
            </div>
            {/* Middle Area */}
            <div className={css(styles.drawerContentArea)}>
              <div className='p-2'>
                {/* Banner */}
                <div className={css(styles.drawerBanner)}>
                  <img
                    ref={drawerBannerRef}
                    src={bannerImageUrl}
                    alt="cropped-banner"
                    className={css(styles.drawerBannerImg)}
                  />
                  {/* Camera icon */}
                  <div
                    onClick={() => { setShowBannerCropperDrawer(true); }}
                    className={css(styles.drawerBannerCameraIcon)}
                  >
                    <i className="fa fa-camera" />
                  </div>
                </div>

                <div>
                  {/* Company Name */}
                  <div>
                    <TextInputWithLabel
                      value={companyName}
                      titleText='Company Name'
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={342}
                      onChange={handleOnEditCompanyName}
                    />
                  </div>
                  {/* User card */}
                  <div className="fx mt-4 ml-2">
                    {/* Avatar */}
                    <div className={css(styles.drawerUserCardAvatar)}>
                      <img
                        src={avatarImageUrl}
                        ref={drawerAvatarRef}
                        alt="avatar"
                        className={css(styles.drawerUserCardAvatarImg)}
                        onClick={() => setShowAvatarCropperDrawer(true)}
                      />
                      <div
                        onClick={() => { setShowAvatarCropperDrawer(true); }}
                        className={css(styles.drawerAvatarCamera)}
                      >
                        <i className="fa fa-camera" />
                      </div>
                    </div>
                    {/* User card displayName & userName */}
                    <div className={`ml-2 ${css(styles.drawerUserCardText)}`}>
                      {/* Display Name */}
                      <div style={{ marginTop: '-5px' }}>
                        <TextInputWithLabel
                          value={displayName}
                          titleText="Display Name (2 to 40 characters)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditDisplayName}
                        />
                        {/* Error message */}
                        {!isDisplayNameValid && !isDisplayNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid Display Name
                          </div>
                          : null
                        }
                      </div>
                      {/* UserName */}
                      <div
                        style={{
                          marginTop: (!isDisplayNameValid && !isDisplayNamePristine) ? '0px' : '4px'
                        }}
                      >
                        <TextInputWithLabel
                          value={userName}
                          titleText="Username (2 to 20 characters, no spaces)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor={blue[500]}
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditUserName}
                        />
                        {/* Error message */}
                        {!isUserNameValid && !isUserNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid User Name
                          </div>
                          : null
                        }
                        {isUserNameValid && !isUserNamePristine && isUserNameDuplicate
                          ? <div className={css(styles.errMsg)}>
                            User Name Taken
                          </div>
                          : null
                        }
                      </div>
                    </div>
                  </div>
                  {/* Mailing Address, Email, Phone, Fax, Profile description & Website */}
                  <div className="mt-4 ml-2">
                    {/* Heading */}
                    <div className="fx fx__v-center">
                      <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                      <div className="ml-1">
                        Mailing Address
                      </div>
                    </div>
                    {/* Form */}
                    <div>
                      {/* Address */}
                      <div className="fx">
                        <div>
                          <TextInputWithLabel
                            value={streetAddress}
                            titleText="Street"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={284}
                            onChange={handleOnEditStreetAddress}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={suite}
                            titleText="Suite"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditSuite}
                          />
                        </div>
                      </div>
                      {/* City, State and Zip */}
                      <div className="fx mt-2">
                        <div>
                          <TextInputWithLabel
                            value={city}
                            titleText="City"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={230}
                            onChange={handleOnEditCity}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={state}
                            titleText="State"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditState}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={zip}
                            titleText="Zip"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditZip}
                          />
                        </div>
                      </div>
                      {/* Email */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={email}
                          titleText="Email*"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-envelope'
                          onChange={handleOnEditEmail}
                        />
                        {!isEmailPristine && !isEmailValid
                          ? <div className={css(styles.errMsg)}>Invalid Email</div>
                          : null
                        }
                        {isEmailValid && !isEmailPristine && isEmailDuplicate
                          ? <div className={css(styles.errMsg)}>Email already in use</div>
                          : null
                        }
                      </div>
                      {/* Phone */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={phone}
                          titleText="Phone #*"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-phone'
                          onChange={handleOnEditPhone}
                        />
                        {!isPhonePristine && !isPhoneValid
                          ? <div className={css(styles.errMsg)}>Invalid Phone #</div>
                          : null
                        }
                      </div>
                      {/* Fax */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={fax}
                          titleText="Fax #"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-fax'
                          onChange={handleOnEditFax}
                        />
                      </div>
                      {/* Website */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={website}
                          titleText="Website"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-globe'
                          onChange={handleOnEditWebsite}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Profile descriptiom */}
                  <div className="mt-4 ml-2">
                    <div>Profile Description</div>
                    <div className={css(styles.profileTextAreaContainer)}>
                      <textarea
                        className={css(styles.profileTextArea)}
                        placeholder="Write a description"
                        value={profileDescription}
                        onChange={handleOnChangeProfileDescription}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSaveCompanyProfile}
                // isDisabled={!canAddCompany}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Create Company Profile
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Banner Upload & Cropper Drawer ---------------------------- */}
      {showBannerCropperDrawer &&
        <Drawer
          placement="right"
          visible={showBannerCropperDrawer}
          toggleDrawer={() => setShowBannerCropperDrawer(false)}
          zIndex="2050"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div>Upload Profile Banner</div>
              <i
                className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => { setShowBannerCropperDrawer(false); }}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
              {showBannerCropper
                ? <div>
                  <Cropper
                    src={bannerImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    aspectRatio={6}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={bannerCropperRef}
                  />
                </div>
                : null
              }
              {!showBannerCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={bannerFileInputRef}
                    onDrop={handleOnBannerFileDrop}
                    onChange={handleOnBannerFileSelected}
                  />
                </div>
                : null
              }
              {isBannerUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Banner Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx pr-2 ${css(styles.drawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickBannerCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Banner Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Avatar Upload & Cropper Drawer ---------------------------- */}
      {showAvatarCropperDrawer &&
        <Drawer
          placement="right"
          visible={showAvatarCropperDrawer}
          toggleDrawer={() => setShowAvatarCropperDrawer(false)}
          zIndex="2050"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div>Upload Profile Avatar</div>
              <i
                className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => { setShowAvatarCropperDrawer(false); }}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
              {showAvatarCropper
                ? <div>
                  <Cropper
                    src={avatarImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    aspectRatio={1}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={avatarCropperRef}
                  />
                </div>
                : null
              }
              {!showAvatarCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={avatarFileInputRef}
                    onDrop={handleOnAvatarFileDrop}
                    onChange={handleOnAvatarFileSelected}
                  />
                </div>
                : null
              }
              {isAvatarUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Avatar Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx pr-2 ${css(styles.drawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickAvatarCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Avatar Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Data Grid & Toolbar container */}
      <div className={css(styles.gridContainer)}>
        {/* Toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Left aligned */}
          <div />
          {/* Right aligned */}
          <div className='fx'>
            {/* Add Company Button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickAddCompany}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div>
            {/* Reset Button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
          </div>
        </div>
        {/* Data grid */}
        {!showCardsGrid &&
          <DataGrid
            loader={showLoader}
            icons={ICONS}
            headerDefs={gridHeaderDefs}
            rowData={gridData}
            filters={filters}
            gridWidth={gridWidth} // was 550
            gridHeight={gridHeight}
            gridHeaderHeight={48}
            gridFooterHeight={0}
            gridContainerHeight='730px'
            rowHeight={64}
            rowsPerPage={DOCS_PER_PAGE}
            defaultCellStyle={DEFAULT_CELL_STYLE}
            showFooter={false}
            showPager
            showEllipsisCol
            enableHover
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            isMobileLayout={isMobileLayout} // window.innerWidth <= 700
            canSelectRows
            onClickCell={handleOnClickCell}
            onPopupMenu={handleOnPopupMenu}
            onFilterTypeUpdate={handleFilterTypeUpdate}
            onApplyFilter={handleOnApplyFilter}
            onColFilterToggle={handleOnFilterToggle}
            onColPinChange={handleOnPinChange}
            onLoadPage={handleOnLoadPage}
            onSort={handleOnSortOrderChanged}
            onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
            onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
            onResize={() => { console.warn('Handler not set for onResize prop.'); }}
            onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
            onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
            onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
            onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
          />
        }
        {/* Cards Grid */}
        {showCardsGrid &&
          <div>
            Not implemented yet!
          </div>
        }
      </div>
    </div>
  );
};

export default CompaniesDataGrid;
